import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2 } from '@/components/forms/Select2';
import { ApiError } from '@/api/client/abpError';
import { usePermission } from '@/permissions/usePermission';
import { groupCategoriesApi } from '@/api/services/onboarding';
import {
  GROUP_EXECUTION_VENUE_MARKET_MAKER,
  type CreateGroupCategoryDto,
  type GroupCategoryDto,
  type GroupCategoryFilterDto,
  type UpdateGroupCategoryDto,
} from '@/api/types/onboarding';

const P = 'User.Onboarding.GroupCategories';

interface Draft {
  name: string;
  currency: string; // currencyId
  dataSource: string; // dataSourceId
  leiCode: string; // LEI string
  miFIDClassification: string;
  clientType: string;
  executionVenue: string;
  assetType: string;
  counterpartyTypeCVA: string;
}

const emptyDraft = (): Draft => ({
  name: '', currency: '', dataSource: '', leiCode: '', miFIDClassification: '',
  clientType: '', executionVenue: '', assetType: '', counterpartyTypeCVA: '',
});

export function GroupCategoriesTab() {
  const toast = useToast();
  const can = usePermission();
  const canCreate = can(`${P}.Create`);
  const canUpdate = can(`${P}.Update`);
  const canSync = can(`${P}.Sync`);

  const dropdowns = useQuery({
    queryKey: ['onboarding', 'group-categories', 'dropdown'],
    queryFn: groupCategoriesApi.getDropdownData,
  });
  const opts = dropdowns.data;

  const [newDraft, setNewDraft] = useState<Draft | null>(null);
  const [editId, setEditId] = useState<string | null>(null); // groupId
  const [editDraft, setEditDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [ddParent, setDdParent] = useState<HTMLElement | null>(null);

  const table = useServerDataTable<GroupCategoryDto, Partial<GroupCategoryFilterDto>>({
    queryKey: ['onboarding', 'group-categories'],
    fetcher: (req) => groupCategoriesApi.getListForDataTable(req as GroupCategoryFilterDto),
    pageSize: 5,
  });

  const currencyOpts = (opts?.currencies ?? []).map((c) => ({ value: c.id, label: c.name }));
  const dsOpts = (opts?.dataSources ?? []).map((d) => ({ value: d.id, label: d.name }));
  const leiOpts = (opts?.leiCodes ?? []).map((l) => ({ value: l.name, label: l.name }));
  const mifidOpts = (opts?.miFIDClassifications ?? []).map((e) => ({ value: e.value, label: e.name }));
  const clientTypeOpts = (opts?.clientTypes ?? []).map((e) => ({ value: e.value, label: e.name }));
  const execVenueOpts = (opts?.executionVenues ?? []).map((e) => ({ value: e.value, label: e.name }));
  const assetTypeOpts = (opts?.assetTypes ?? []).map((e) => ({ value: e.value, label: e.name }));
  const counterpartyOpts = (opts?.counterpartyTypes ?? []).map((e) => ({ value: e.value, label: e.name }));
  const clientLei = opts?.clientLeiCode ?? null;

  // Market Maker (execution venue value 0) → force LEI to the tenant LEI + disable the select.
  const isMarketMaker = (execVenueValue: string) => Number(execVenueValue) === GROUP_EXECUTION_VENUE_MARKET_MAKER;
  const applyVenue = (d: Draft, venue: string): Draft => {
    if (isMarketMaker(venue) && clientLei != null) return { ...d, executionVenue: venue, leiCode: clientLei };
    return { ...d, executionVenue: venue };
  };

  const saveNew = async () => {
    if (!newDraft) return;
    setSaving(true);
    try {
      const dto: CreateGroupCategoryDto = {
        name: newDraft.name,
        currencyId: Number.parseInt(newDraft.currency, 10),
        miFIDClassification: Number.parseInt(newDraft.miFIDClassification, 10),
        clientType: Number.parseInt(newDraft.clientType, 10),
        executionVenue: Number.parseInt(newDraft.executionVenue, 10),
        leiCode: newDraft.leiCode || null,
        assetType: Number.parseInt(newDraft.assetType, 10),
        counterpartyTypeCVA: Number.parseInt(newDraft.counterpartyTypeCVA, 10),
        dataSourceId: Number(newDraft.dataSource),
      };
      await groupCategoriesApi.create(dto);
      toast.success('New Group Added Successfully');
      setNewDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to add group.');
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (row: GroupCategoryDto) => {
    setEditId(row.groupId);
    setEditDraft({
      name: row.name,
      currency: String(row.currencyId),
      dataSource: String(row.dataSourceId),
      leiCode: row.leiCode ?? '',
      miFIDClassification: String(mifidOpts.find((o) => o.label === row.miFIDClassification)?.value ?? ''),
      clientType: String(clientTypeOpts.find((o) => o.label === row.clientType)?.value ?? ''),
      executionVenue: String(execVenueOpts.find((o) => o.label === row.executionVenue)?.value ?? ''),
      assetType: String(assetTypeOpts.find((o) => o.label === row.assetType)?.value ?? ''),
      counterpartyTypeCVA: String(counterpartyOpts.find((o) => o.label === row.counterpartyTypeCVA)?.value ?? ''),
    });
  };

  const saveEdit = async () => {
    if (!editId || !editDraft) return;
    setSaving(true);
    try {
      const dto: UpdateGroupCategoryDto = {
        miFIDClassification: Number.parseInt(editDraft.miFIDClassification, 10),
        clientType: Number.parseInt(editDraft.clientType, 10),
        executionVenue: Number.parseInt(editDraft.executionVenue, 10),
        leiCode: editDraft.leiCode || null,
        assetType: Number.parseInt(editDraft.assetType, 10),
        counterpartyTypeCVA: Number.parseInt(editDraft.counterpartyTypeCVA, 10),
      };
      await groupCategoriesApi.update(editId, dto);
      toast.success('Saved successfully.');
      setEditId(null);
      setEditDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update group.');
    } finally {
      setSaving(false);
    }
  };

  const syncGroups = async () => {
    try {
      const result = await groupCategoriesApi.fetchGroupsFromDataSources();
      toast.success(result || 'Groups synchronized successfully.');
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to sync groups.');
    }
  };

  const sel = (val: string, options: { value: string | number; label: string }[], onChange: (v: string) => void, disabled = false) => (
    <Select2
      value={val}
      options={options}
      placeholder="N/A"
      allowClear={false}
      searchable={false}
      disabled={saving || disabled}
      dropdownParent={ddParent}
      onChange={(v) => onChange(v ?? '')}
    />
  );

  const columns = useMemo<DataGridColumn<GroupCategoryDto>[]>(
    () => [
      { key: 'name', title: 'Group Name', className: 'text-start', value: (r) => r.name },
      { key: 'currency', title: 'Currency', className: 'text-start', value: (r) => r.currency },
      {
        key: 'miFIDClassification', title: 'MiFID Classification', className: 'text-start', value: (r) => r.miFIDClassification,
        cell: (r) => (editId === r.groupId && editDraft ? sel(editDraft.miFIDClassification, mifidOpts, (v) => setEditDraft({ ...editDraft, miFIDClassification: v })) : r.miFIDClassification),
      },
      {
        key: 'clientType', title: 'Client Type', className: 'text-start', value: (r) => r.clientType,
        cell: (r) => (editId === r.groupId && editDraft ? sel(editDraft.clientType, clientTypeOpts, (v) => setEditDraft({ ...editDraft, clientType: v })) : r.clientType),
      },
      {
        key: 'executionVenue', title: 'Execution Venue', className: 'text-start', value: (r) => r.executionVenue,
        cell: (r) => (editId === r.groupId && editDraft ? sel(editDraft.executionVenue, execVenueOpts, (v) => setEditDraft(applyVenue(editDraft, v))) : r.executionVenue),
      },
      {
        key: 'leiCode', title: 'LEI Code', className: 'text-start', value: (r) => r.leiCode ?? '',
        cell: (r) => (editId === r.groupId && editDraft ? sel(editDraft.leiCode, leiOpts, (v) => setEditDraft({ ...editDraft, leiCode: v }), isMarketMaker(editDraft.executionVenue)) : (r.leiCode ?? '')),
      },
      {
        key: 'assetType', title: 'Client Assets Type', className: 'text-start', value: (r) => r.assetType,
        cell: (r) => (editId === r.groupId && editDraft ? sel(editDraft.assetType, assetTypeOpts, (v) => setEditDraft({ ...editDraft, assetType: v })) : r.assetType),
      },
      {
        key: 'counterpartyTypeCVA', title: 'Counterparty Type', className: 'text-start', value: (r) => r.counterpartyTypeCVA,
        cell: (r) => (editId === r.groupId && editDraft ? sel(editDraft.counterpartyTypeCVA, counterpartyOpts, (v) => setEditDraft({ ...editDraft, counterpartyTypeCVA: v })) : r.counterpartyTypeCVA),
      },
      { key: 'dataSource', title: 'Connector', className: 'text-start', value: (r) => r.dataSource },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editId, editDraft, saving, opts],
  );

  const toolbar = (
    <div className="d-flex gap-2">
      {canCreate && (
        <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" title="Add new group category" onClick={() => { if (!newDraft) setNewDraft(emptyDraft()); }}>
          Add New
        </AsyncButton>
      )}
      {canSync && (
        <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-sync" title="Sync groups from data sources" onClick={syncGroups}>
          Fetch New Groups
        </AsyncButton>
      )}
    </div>
  );

  const footerTop = newDraft ? (
    <tr className="onb-new-row">
      <td><input className="form-control form-control-sm" placeholder="Group Name" value={newDraft.name} disabled={saving} onChange={(e) => setNewDraft({ ...newDraft, name: e.target.value })} /></td>
      <td>{sel(newDraft.currency, currencyOpts, (v) => setNewDraft({ ...newDraft, currency: v }))}</td>
      <td>{sel(newDraft.miFIDClassification, mifidOpts, (v) => setNewDraft({ ...newDraft, miFIDClassification: v }))}</td>
      <td>{sel(newDraft.clientType, clientTypeOpts, (v) => setNewDraft({ ...newDraft, clientType: v }))}</td>
      <td>{sel(newDraft.executionVenue, execVenueOpts, (v) => setNewDraft(applyVenue(newDraft, v)))}</td>
      <td>{sel(newDraft.leiCode, leiOpts, (v) => setNewDraft({ ...newDraft, leiCode: v }), isMarketMaker(newDraft.executionVenue))}</td>
      <td>{sel(newDraft.assetType, assetTypeOpts, (v) => setNewDraft({ ...newDraft, assetType: v }))}</td>
      <td>{sel(newDraft.counterpartyTypeCVA, counterpartyOpts, (v) => setNewDraft({ ...newDraft, counterpartyTypeCVA: v }))}</td>
      <td>{sel(newDraft.dataSource, dsOpts, (v) => setNewDraft({ ...newDraft, dataSource: v }))}</td>
      <td>
        <div className="d-flex gap-1">
          <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveNew} disabled={saving} />
          <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => setNewDraft(null)}><i className="fa fa-times" /></button>
        </div>
      </td>
    </tr>
  ) : undefined;

  const rowActions = canUpdate
    ? (row: GroupCategoryDto) => (
        <div className="d-flex gap-1">
          {editId !== row.groupId && (
            <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => beginEdit(row)}><i className="fa fa-pencil" /></button>
          )}
          {editId === row.groupId && (
            <>
              <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveEdit} disabled={saving} />
              <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => { setEditId(null); setEditDraft(null); }}><i className="fa fa-times" /></button>
            </>
          )}
        </div>
      )
    : undefined;

  return (
    <div ref={setDdParent}>
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        toolbarStart={toolbar}
        includeExcel
        excelFileName="Group Categories"
        footerTop={footerTop}
        rowActions={rowActions}
        rowClassName={(r) => (editId === r.groupId ? 'sfc-detail-editing' : undefined)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />
    </div>
  );
}
